import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

const COOLDOWN_MS = 60_000; // 1 minute cooldown after refresh

interface HeaderProps {
  title: string;
  lastFetched: Date | null;
  onRefresh: () => void;
  loading: boolean;
}

export function Header({ title, lastFetched, onRefresh, loading }: HeaderProps) {
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const inCooldown = cooldownRemaining > 0;

  useEffect(() => {
    if (!lastRefreshTime) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, COOLDOWN_MS - (Date.now() - lastRefreshTime));
      setCooldownRemaining(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [lastRefreshTime]);

  const handleRefresh = useCallback(() => {
    setLastRefreshTime(Date.now());
    setCooldownRemaining(COOLDOWN_MS);
    onRefresh();
  }, [onRefresh]);

  const cooldownSeconds = Math.ceil(cooldownRemaining / 1000);

  return (
    <header className="flex h-14 items-center justify-between border-b px-6">
      <h1 className="text-lg font-semibold">{title}</h1>
      <div className="flex items-center gap-3">
        {lastFetched && (
          <span className="text-muted-foreground text-sm">
            Cached {formatRelativeTime(lastFetched)}
          </span>
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={loading || inCooldown}
                >
                  <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
                  {inCooldown ? `${cooldownSeconds}s` : 'Refresh'}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              style={{
                backgroundColor: 'var(--card)',
                color: 'var(--foreground)',
                borderColor: 'var(--border)',
              }}
              className="max-w-xs border p-3 shadow-lg"
            >
              <p className="text-sm">
                Clears all server-side caches and re-fetches live data from AWS.
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                Cost data is cached for 4 hours to minimize API costs ($0.01/request).
                Infrastructure metrics refresh every 2–30 min.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </header>
  );
}
